import { ChevronDownIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../ui/menu";

import { Button } from "../ui/button";

import { useOptionalSettingsScope } from "./SettingsScopeContext";
import {
  ALL_ENVIRONMENTS_VALUE,
  ALL_PROJECTS_VALUE,
  environmentAxisValue,
  projectAxisValue,
  selectEnvironmentAxis,
  selectProjectAxis,
  settingsScopeEnvironmentLabel,
} from "./settingsScopeAxis";

/**
 * "Applying settings for <project> across <environment>" at the top of a
 * settings page, with the two pickers that change the answer.
 *
 * Adapted from upstream's `SettingsScopeSentence.tsx` (pingdotgg/t3code,
 * MIT, pin e3e7cc3f). Each connective travels with its picker so a wrap
 * never strands the word "across" at a line's end.
 *
 * @module
 */

/**
 * Pages whose every row is saved on this device; they have no scope to pick,
 * so the sentence would only ask a question it cannot act on. The route's
 * scope boundary reads the same set: a page that ignores the scope must not
 * be replaced by an explanation of it.
 */
export const SETTINGS_DEVICE_ONLY_PATHS: ReadonlySet<string> = new Set([
  "/settings/appearance",
  "/settings/keybindings",
  "/settings/environments",
  "/settings/archived",
]);

function ScopeMenu(props: {
  readonly ariaLabel: string;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <Menu>
      <MenuTrigger
        aria-label={`${props.ariaLabel}: ${props.label}`}
        render={<Button size="sm" variant="ghost" className="max-w-64 gap-1.5 px-2 font-normal" />}
      >
        <span className="min-w-0 truncate">{props.label}</span>
        <ChevronDownIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      </MenuTrigger>
      <MenuPopup align="start">{props.children}</MenuPopup>
    </Menu>
  );
}

function ProjectScopeMenu(): ReactNode {
  const scope = useOptionalSettingsScope();
  if (scope === null) return null;
  const selected = scope.groups.find((group) => group.projectKey === scope.search.project);
  return (
    <ScopeMenu
      ariaLabel="Project scope"
      label={
        selected?.displayName ?? (scope.search.project ? "Unavailable project" : "All projects")
      }
    >
      <MenuRadioGroup
        value={projectAxisValue(scope.search)}
        onValueChange={(next) => {
          if (typeof next === "string") scope.selectScope(selectProjectAxis(scope.search, next));
        }}
      >
        <MenuRadioItem hideIndicator value={ALL_PROJECTS_VALUE}>
          All projects
        </MenuRadioItem>
        {scope.groups.map((group) => (
          <MenuRadioItem hideIndicator key={group.projectKey} value={group.projectKey}>
            <span className="min-w-0 truncate">{group.displayName}</span>
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </ScopeMenu>
  );
}

function EnvironmentScopeMenu(): ReactNode {
  const scope = useOptionalSettingsScope();
  if (scope === null) return null;
  const selected = scope.environments.find(
    (environment) => environment.environmentId === environmentAxisValue(scope.search),
  );
  return (
    <ScopeMenu
      ariaLabel="Environment scope"
      label={
        selected
          ? settingsScopeEnvironmentLabel(selected, scope.environments)
          : scope.search.machine
            ? "Unavailable environment"
            : "All environments"
      }
    >
      <MenuRadioGroup
        value={environmentAxisValue(scope.search)}
        onValueChange={(next) => {
          if (typeof next === "string")
            scope.selectScope(selectEnvironmentAxis(scope.search, next));
        }}
      >
        <MenuRadioItem hideIndicator value={ALL_ENVIRONMENTS_VALUE}>
          All environments
        </MenuRadioItem>
        {scope.environments.map((environment) => (
          <MenuRadioItem
            hideIndicator
            key={environment.environmentId}
            value={environment.environmentId}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="min-w-0 truncate">
                {settingsScopeEnvironmentLabel(environment, scope.environments)}
              </span>
              {environment.online ? null : (
                <span className="shrink-0 text-muted-foreground text-xs">Offline</span>
              )}
            </span>
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </ScopeMenu>
  );
}

/**
 * The sentence itself. Hidden on device-only pages and on a stale selection
 * (the route's boundary message replaces it there), so it only renders when
 * both pickers can name a real target.
 */
export function SettingsScopeSentence(): ReactNode {
  const scope = useOptionalSettingsScope();
  if (scope === null || SETTINGS_DEVICE_ONLY_PATHS.has(scope.pathname)) return null;
  if (scope.scope.kind === "unavailable") return null;
  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 px-3 text-muted-foreground text-sm sm:px-4">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="shrink-0">Applying settings for</span>
        <ProjectScopeMenu />
      </span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="shrink-0">across</span>
        <EnvironmentScopeMenu />
      </span>
    </p>
  );
}
